import React, { useState, useEffect, useMemo } from 'react';
import { getFeatureImportanceData, pathwayAPI } from '../api';
import { EmptyState, InfoTooltip, ErrorState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';
import TargetAnnotations from './TargetAnnotations';
import TargetScorecardTable from './shared/TargetScorecardTable';
import {
  SpecificityBadgeLegend,
  SpecificitySummaryBanner,
  SpecificityBadges,
  ScoreCell,
  SpecificityInterpretationText,
  StructuredEvidencePanel,
  SortableTableHeader,
  UnifiedPathwayTable,
} from './shared/SpecificityInterpretation';

const pathwayInterpretationCopy = {
  general: {
    title: 'How to read general pathway enrichment',
    points: [
      'Lower p_value and significance_fdr indicate stronger statistical support.',
      'Use this view to identify broad biological processes linked to top predictive genes.',
      'Specificity badges appear when pathway rows include reference-panel scores from the backend.',
    ],
  },
  senescence: {
    title: 'How to read senescence enrichment',
    points: [
      'matching_genes lists genes from your set that match the senescence reference.',
      'A higher count indicates stronger senescence-related signal in your selected genes.',
    ],
  },
  unified: {
    title: 'How to read unified pathway enrichment',
    points: [
      'combined_pathway_score summarizes evidence strength across pathway sources.',
      'consensus_count shows how many sources support the pathway.',
      'AD specificity, aging, inflammation, and cross-neurodegeneration scores show reference-panel overlap for each pathway.',
      'Interpretation text explains whether a pathway is likely AD-specific or reflects competing biology.',
    ],
  },
};

const PathwayInterpretation = ({ pathwayType }) => {
  const copy = pathwayInterpretationCopy[pathwayType] || pathwayInterpretationCopy.general;
  return (
    <div style={{ marginBottom: '12px', border: '1px solid #e6ecf5', backgroundColor: '#f7faff', borderRadius: '8px', padding: '12px' }}>
      <div style={{ fontWeight: 600, marginBottom: '8px' }}>{copy.title}</div>
      <ul style={{ margin: 0, paddingLeft: '20px' }}>
        {copy.points.map((point) => (
          <li key={point} style={{ marginBottom: '4px' }}>{point}</li>
        ))}
      </ul>
    </div>
  );
};

const PathwayTable = ({ rows, pathwayType }) => {
  if (!rows || rows.length === 0) return <p>No pathways found.</p>;
  if (pathwayType === 'unified' && rows.some((row) => row.specificity_badges || row.ad_specificity_score != null)) {
    return <UnifiedPathwayTable rows={rows} />;
  }
  if (typeof rows[0] === 'string') {
    return (
      <ul style={{ margin: 0, paddingLeft: 20, marginTop: '16px' }}>
        {rows.map((item, idx) => <li key={idx}>{item}</li>)}
      </ul>
    );
  }
  const headers = Object.keys(rows[0]);
  return (
    <div style={{ overflowX: 'auto', marginTop: '16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {headers.map((header) => {
                let value = row[header];

                if (typeof value === 'number' && (header.includes('p_value') || header.includes('fdr'))) {
                  value = value.toExponential(3);
                }

                return (
                  <td
                    key={header}
                    style={{
                      borderBottom: '1px solid #eee',
                      padding: '8px',
                      verticalAlign: 'top',
                    }}
                  >
                    {header === 'specificity_badges' && Array.isArray(value) && value.every((item) => typeof item === 'string') ? (
                      <SpecificityBadges badges={value} />
                    ) : header === 'specificity_interpretation' && typeof value === 'string' ? (
                      <SpecificityInterpretationText row={row} />
                    ) : Array.isArray(value) ? (
                      value.every((item) => typeof item !== 'object') ? (
                        value.join(', ')
                      ) : (
                        <details>
                          <summary style={{ cursor: 'pointer', color: '#0056b3' }}>
                            View Details ({value.length})
                          </summary>
                          <pre
                            style={{
                              marginTop: 8,
                              whiteSpace: 'pre-wrap',
                              fontSize: 12,
                              background: '#f6f8fa',
                              padding: 8,
                              borderRadius: 6,
                              maxWidth: 500,
                              overflowX: 'auto',
                            }}
                          >
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        </details>
                      )
                    ) : typeof value === 'object' && value !== null ? (
                      <details>
                        <summary style={{ cursor: 'pointer', color: '#0056b3' }}>
                          View Object
                        </summary>
                        <pre
                          style={{
                            marginTop: 8,
                            whiteSpace: 'pre-wrap',
                            fontSize: 12,
                            background: '#f6f8fa',
                            padding: 8,
                            borderRadius: 6,
                            maxWidth: 500,
                            overflowX: 'auto',
                          }}
                        >
                          {JSON.stringify(value, null, 2)}
                        </pre>
                      </details>
                    ) : (
                      value ?? ''
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SORTABLE_COLUMNS = new Set([
  'importance',
  'ad_specificity_score',
  'aging_association_score',
  'neuroinflammation_overlap_score',
  'cross_neurodegeneration_overlap_score',
]);

function FeatureImportance({ datasetName, modelType }) {
  const [featureImportance, setFeatureImportance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('importance');
  const [sortOrder, setSortOrder] = useState('desc');

  const [loadingPathway, setLoadingPathway] = useState(false);
  const [pathwayError, setPathwayError] = useState(null);
  const [pathwayData, setPathwayData] = useState(null);
  const [analyzedFeatures, setAnalyzedFeatures] = useState([]);
  const [pathwayType, setPathwayType] = useState('general');

  const [topGenesCount, setTopGenesCount] = useState(50);
  const [topPathwaysN, setTopPathwaysN] = useState(5);

  const handleGetFeatureImportance = async () => {
    setLoading(true);
    setError(null);
    setFeatureImportance(null);
    setPathwayData(null);
    try {
      if (!datasetName || !modelType) {
        throw new Error('Select a dataset and model type to load feature importance.');
      }
      const result = await getFeatureImportanceData(datasetName, modelType);
      setFeatureImportance(Array.isArray(result) ? result : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (datasetName && modelType) {
      handleGetFeatureImportance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetName, modelType]);

  const sortedFeatureImportance = useMemo(() => {
    if (!Array.isArray(featureImportance) || !featureImportance.length) return [];

    const rows = [...featureImportance];
    rows.sort((a, b) => {
      const left = a[sortBy];
      const right = b[sortBy];
      const leftNum = typeof left === 'number' && Number.isFinite(left) ? left : null;
      const rightNum = typeof right === 'number' && Number.isFinite(right) ? right : null;

      if (leftNum === null && rightNum === null) return 0;
      if (leftNum === null) return 1;
      if (rightNum === null) return -1;

      return sortOrder === 'asc' ? leftNum - rightNum : rightNum - leftNum;
    });
    return rows;
  }, [featureImportance, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (!SORTABLE_COLUMNS.has(column)) return;
    if (sortBy === column) {
      setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleFetchPathways = async () => {
    if (!featureImportance) return;

    setLoadingPathway(true);
    setPathwayError(null);
    setPathwayData(null);
    setAnalyzedFeatures([]);

    try {
      let allFeatures = [];

      if (Array.isArray(featureImportance)) {
        allFeatures = featureImportance.map((feature) => feature.feature || feature.name || Object.keys(feature)[0]);
      }

      const cleanedGenes = allFeatures.filter(
        (gene) => gene && !gene.includes(' ') && !gene.toLowerCase().startsWith('is_')
      );

      const genesToSend = cleanedGenes.slice(0, topGenesCount);

      if (genesToSend.length === 0) {
        throw new Error('Could not find any valid gene symbols in the top features.');
      }

      setAnalyzedFeatures(genesToSend);

      let data;
      if (pathwayType === 'senescence') {
        data = await pathwayAPI.senescencePathways(genesToSend, topPathwaysN);
      } else if (pathwayType === 'unified') {
        data = await pathwayAPI.unifiedEnrichPathways(genesToSend, topPathwaysN);
      } else {
        data = await pathwayAPI.enrichPathways(genesToSend, topPathwaysN);
      }

      setPathwayData(data.pathways || data.matching_genes || data || []);
    } catch (err) {
      console.error('Pathway Error:', err);
      setPathwayError(err.message || 'Failed to fetch pathway enrichment.');
    } finally {
      setLoadingPathway(false);
    }
  };

  return (
    <div className="feature-importance">
      <InfoTooltip
        title="Feature Importance"
        content="View which features contribute most to your model's predictions. Specificity scores and badges show whether each target overlaps AD, aging, inflammation, or cross-neurodegeneration seed reference panels."
      >
        <h2 className="feature-importance__title">Feature Importance</h2>
      </InfoTooltip>

      <button
        onClick={handleGetFeatureImportance}
        disabled={loading}
        className="feature-importance__load-btn"
      >
        {loading ? 'Loading...' : 'Get Feature Importance'}
      </button>

      {loading && (
        <div className="feature-importance__loading">
          <LoadingSpinner text="Loading feature importance..." />
        </div>
      )}

      {error && (
        <ErrorState
          message={error}
          onRetry={() => {
            setError(null);
            handleGetFeatureImportance();
          }}
        />
      )}

      {!featureImportance && !loading && !error && (
        <EmptyState
          message={datasetName && modelType
            ? 'Click the button above to (re)load feature importance for the selected dataset and model.'
            : 'Select a dataset and model to view feature importance.'}
          icon="📈"
        />
      )}

      {featureImportance && (
        <div className="feature-importance__results">
          <h3 className="feature-importance__results-title">Feature Importance Rankings</h3>

          <SpecificitySummaryBanner rows={sortedFeatureImportance} />
          <SpecificityBadgeLegend />

          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Target</th>
                  <SortableTableHeader
                    label="Importance"
                    column="importance"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  >
                    Importance
                  </SortableTableHeader>
                  <SortableTableHeader
                    label="AD Specificity"
                    column="ad_specificity_score"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Aging"
                    column="aging_association_score"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Inflammation"
                    column="neuroinflammation_overlap_score"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableTableHeader
                    label="Cross-ND"
                    column="cross_neurodegeneration_overlap_score"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Badges</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Interpretation</th>
                  <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px 6px' }}>Evidence</th>
                </tr>
              </thead>
              <tbody>
                {sortedFeatureImportance.map((row, idx) => (
                  <tr key={`${row.feature || row.name || idx}`}>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px', fontWeight: 600 }}>
                      {row.feature || row.name}
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      {typeof row.importance === 'number' ? row.importance.toFixed(6) : '—'}
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <ScoreCell score={row.ad_specificity_score} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <ScoreCell score={row.aging_association_score} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <ScoreCell score={row.neuroinflammation_overlap_score} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <ScoreCell score={row.cross_neurodegeneration_overlap_score} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <SpecificityBadges badges={row.specificity_badges} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px', minWidth: 220 }}>
                      <SpecificityInterpretationText row={row} />
                    </td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '8px 6px' }}>
                      <StructuredEvidencePanel evidence={row.specificity_evidence} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TargetScorecardTable
        datasetName={datasetName}
        modelType={modelType}
        featureImportance={featureImportance}
      />

      {featureImportance && (
        <div style={{ padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: '#fff' }}>
          <InfoTooltip
            title="Biological Context (Pathways)"
            content="Translate your top predictive features into known biological pathways using Reactome."
          >
            <h2 style={{ marginTop: 0, marginBottom: '16px' }}>Biological Context (Pathways)</h2>
          </InfoTooltip>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>Analysis Type:</label>
              <select value={pathwayType} onChange={(e) => setPathwayType(e.target.value)} style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc' }}>
                <option value="general">General Pathways</option>
                <option value="senescence">Senescence Pathways</option>
                <option value="unified">Unified Pathways</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>Features to Analyze:</label>
              <select value={topGenesCount} onChange={(e) => setTopGenesCount(Number(e.target.value))} style={{ padding: '4px 8px' }}>
                <option value={20}>Top 20</option>
                <option value={50}>Top 50</option>
                <option value={100}>Top 100</option>
                <option value={200}>Top 200</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>Results to Show:</label>
              <select value={topPathwaysN} onChange={(e) => setTopPathwaysN(Number(e.target.value))} style={{ padding: '4px 8px' }}>
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value={20}>Top 20</option>
              </select>
            </div>

            <button
              onClick={handleFetchPathways}
              disabled={loadingPathway}
              style={{
                padding: '8px 16px',
                backgroundColor: loadingPathway ? '#ccc' : '#0056b3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingPathway ? 'not-allowed' : 'pointer',
                marginTop: '16px',
              }}
            >
              {loadingPathway ? 'Analyzing Pathways...' : 'Find Associated Pathways'}
            </button>
          </div>

          {loadingPathway && <LoadingSpinner text="Querying Databases..." />}
          {pathwayError && <ErrorState message={pathwayError} onRetry={handleFetchPathways} />}

          {!loadingPathway && !pathwayError && pathwayData && (
            <div>
              <PathwayInterpretation pathwayType={pathwayType} />
              <details style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--text-muted, #555)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  View the {analyzedFeatures.length} valid features used for this analysis
                </summary>
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: 'var(--bg-light, #f8f9fa)', borderRadius: '4px', border: '1px solid var(--border)', lineHeight: '1.5' }}>
                  {analyzedFeatures.join(', ')}
                </div>
              </details>
              <PathwayTable rows={pathwayData} pathwayType={pathwayType} />
            </div>
          )}
        </div>
      )}
      <TargetAnnotations featureImportance={featureImportance} />
    </div>
  );
}

export default FeatureImportance;
